"use client";
import { useAuthMutations } from "@/api/hooks/authHooks";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { AuthBrandMark } from "@/components/auth/brand-mark";

import { createRegisterSchema } from "@membentry/shared-validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FormInput } from "./FormInput";
import { AppleSignInButton, GoogleSignInButton } from "./auth/social-buttons";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { validateAuthReturnPath } from "@/lib/auth-return-path";
import type { ComponentProps } from "react";
import { toast } from "sonner";
import { resolveAppLocale } from "@membentry/shared-types";

// Owner decision: phone and emergency contact stay outside the minimal register contract; keep them recoverable.
const SHOW_PHONE_INPUT = false;

export function RegisterForm({ className, ...props }: ComponentProps<"div">) {
  const t = useTranslations();
  const params = useParams();
  const locale = params.locale as string;
  const router = useRouter();
  const returnTo = validateAuthReturnPath(useSearchParams().get("returnTo"));
  const registerSchema = createRegisterSchema((key, values) =>
    t(`validation.${key}` as never, values),
  );

  type RegisterFormValues = z.infer<typeof registerSchema>;

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
      ...(SHOW_PHONE_INPUT
        ? { phoneNumber: "", emergencyContactName: "", emergencyContactPhone: "" }
        : {}),
    },
  });
  const { registerMutation } = useAuthMutations();

  function onSubmit(values: RegisterFormValues) {
    const language = resolveAppLocale(navigator.languages.length ? navigator.languages : [navigator.language]);
    registerMutation.mutate({ ...values, language }, {
      onSuccess: () => {
        toast.success(t("auth.registerSuccess"));
        const authUrl = `/${locale}/auth?email=${encodeURIComponent(values.email)}`;
        router.replace(returnTo ? `${authUrl}&returnTo=${encodeURIComponent(returnTo)}` : authUrl);
      },
    });
  }

  return (
    <div className={cn("flex flex-col gap-7 sm:gap-8", className)} {...props}>
      <AuthBrandMark alt={t("auth.logoAlt")} />
      <Card className="glass-panel border-white/60 max-sm:gap-0 max-sm:border-0 max-sm:bg-transparent! max-sm:py-0 max-sm:shadow-none! max-sm:backdrop-blur-none!">
        <CardHeader className="px-0 sm:px-6">
          <CardTitle className="text-[1.85rem] sm:text-3xl leading-tight tracking-tight">
            <h1>{t("auth.register")}</h1>
          </CardTitle>
          <CardDescription className="max-w-sm text-slate-400 text-sm sm:text-sm leading-6">
            {t("auth.fillDetails")}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <div className="gap-5 sm:gap-6 grid">
                <FormInput
                  name="email"
                  control={form.control}
                  label={t("auth.email")}
                  placeholder={t("auth.emailPlaceholder")}
                  type="email"
                />
                <FormInput
                  name="firstName"
                  control={form.control}
                  label={t("auth.firstName")}
                  placeholder={t("auth.yourFirstName")}
                />
                <FormInput
                  name="lastName"
                  control={form.control}
                  label={t("auth.lastName")}
                  placeholder={t("auth.yourLastName")}
                />
                {SHOW_PHONE_INPUT && (
                  <>
                    <FormInput
                      name="phoneNumber"
                      control={form.control}
                      label={t("auth.phoneNumber")}
                      placeholder={t("auth.phonePlaceholder")}
                      type="tel"
                    />
                    <FormInput
                      name="emergencyContactName"
                      control={form.control}
                      label={t("me.account.emergencyContactName")}
                    />
                    <FormInput
                      name="emergencyContactPhone"
                      control={form.control}
                      label={t("me.account.emergencyContactPhone")}
                      placeholder={t("auth.phonePlaceholder")}
                      type="tel"
                    />
                  </>
                )}

                <FormInput
                  name="password"
                  control={form.control}
                  label={t("auth.password")}
                  placeholder={t("auth.yourPassword")}
                  type="password"
                />
                <FormInput
                  name="confirmPassword"
                  control={form.control}
                  label={t("auth.confirmPassword")}
                  placeholder={t("auth.reEnterPassword")}
                  type="password"
                />
                <div className="flex flex-col gap-3">
                  <Button
                    type="submit"
                    className="w-full h-11"
                    disabled={registerMutation.isPending}
                  >
                    {registerMutation.isPending
                      ? t("auth.registering")
                      : t("auth.register")}
                  </Button>
                </div>
              </div>
              <div className="mt-2 text-sm text-center">
                {t("auth.haveAccount")}{" "}
                <Link
                  href={`/${locale}/auth`}
                  className="underline underline-offset-4"
                >
                  {t("auth.login")}
                </Link>
              </div>
            </form>
          </Form>
          <div className="mt-6 flex flex-col gap-3">
            <GoogleSignInButton />
            <AppleSignInButton withDivider={false} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
